"use client";

import { useState } from "react";
import CostCalculator from "@/components/home/CostCalculator/CostCalculator";
import ComparePlans from "@/components/pricing/compare/ComparePlans";
import AiToolsFaq, {
  type IFaqList,
} from "@/components/tools/ToolsCommon/AiToolsFaq";
import PricingCardsArea from "@/components/pricing/PricingCards/PricingCardsArea";
import SearchPricingCardsArea from "@/components/pricing/PricingCards/SearchPricingCardsArea";
import bgHeroImage from "@/assets/images/pricing/hero-background.svg";
import CommonTitle from "@/components/common/CommonTitle";
import CommonSubtitle from "@/components/common/CommonSubtitle";
import Achievements from "@/components/home/Certifications/Achievements";
import CustomButton from "@/components/common/CustomButton";
import { BookDemoHubspotButtonUrl } from "@/utils/hubspot-contants";

const pricingFaqList: IFaqList[] = [
  {
    qid: "pricing-1",
    title: "Is there a free version of Kroolo and what does it include?",
    answer:
      "Yes - Kroolo offers a Starter plan that is Free Forever. This free plan includes: 1 Workspace, 5 Projects, Unlimited tasks, Up to 5 members, 5 Goals, 5 Teams, and 5 Channels (with unlimited chat), and Generative AI features included. This makes the free plan a solid option for individuals or very small teams who want to try Kroolo's core work management and AI capabilities without cost.",
  },
  {
    qid: "pricing-2",
    title: "Can I try Kroolo's paid plans before committing?",
    answer:
      "Yes - Kroolo's Plus plan includes a 14-day free trial so you can evaluate advanced features before subscribing. During the trial, you get access to the Plus plan's higher limits and capabilities.",
  },
  {
    qid: "pricing-3",
    title: "Can I switch between plans at any time?",
    answer:
      "Yes - you can switch between plans at any time. Kroolo's pricing structure is designed so you can start with the free plan and upgrade later, or move between Plus, Business, or Enterprise tiers as your needs change.",
  },
  {
    qid: "pricing-4",
    title: "Do paid plans include Generative AI features?",
    answer:
      "Yes - all of Kroolo's plans, including Starter (free), Plus, and Business, include Generative AI features as part of their offerings. These AI capabilities are integrated into the platform across task, project, and document workflows.",
  },
  {
    qid: "pricing-5",
    title: "Do you offer custom enterprise pricing and features?",
    answer:
      "Yes - Kroolo offers an Enterprise plan that includes custom pricing and features tailored for larger teams and organizations. Enterprise customers get advanced capabilities like custom roles, enterprise AI, single sign-on (SSO), data residency options, live onboarding & training, and a dedicated customer success manager. Contacting sales is required to get specific pricing and a custom demo.",
  },
];

const pricingFaqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: pricingFaqList.map((faq) => ({
    "@type": "Question",
    name: faq.title,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

const Pricing = () => {
  const [selectedProduct, setSelectedProduct] = useState<string>("work");

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pricingFaqSchema) }}
      />
      <section
        className="relative min-h-screen bg-[#FAFAFA] pt-[80px]"
        style={{
          backgroundImage: `url(${bgHeroImage.src})`,
          backgroundColor: "#202020",
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="pb-24 pt-12 xl:pb-28">
          <div className="container relative z-10 mx-auto px-4 xl:px-6">
            <div className="flex flex-col items-center gap-6 text-center">
              <CommonTitle
                as="h1"
                color="#202020"
                fontSizeClass="text-[36px] lg:text-[48px] mx-auto max-w-[580px] text-center"
                fontWeightClass="font-bold"
              >
                One Platform. <br className="hidden lg:block" /> {" "}
                <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                Infinite Possibilities.
                </span>
              </CommonTitle>
              <CommonSubtitle>
                Replace your entire stack with the only AI-first platform for
                Work and Search.
              </CommonSubtitle>
              <div className="inline-flex rounded-full border border-[#ECECEC] bg-white p-0.5">
                <button
                  onClick={() => setSelectedProduct("work")}
                  className={`min-w-[140px] rounded-full px-4 py-2.5 text-[14px] font-medium transition-all duration-200 sm:min-w-[160px] sm:text-[16px] ${selectedProduct === "work"
                    ? "bg-[#202020] text-white"
                    : "bg-transparent text-[#202020]"
                    }`}
                >
                  Kroolo Work
                </button>
                <button
                  onClick={() => setSelectedProduct("search")}
                  className={`min-w-[140px] rounded-full px-4 py-2.5 text-[14px] font-medium transition-all duration-200 sm:min-w-[160px] sm:text-[16px] ${selectedProduct === "search"
                    ? "bg-[#202020] text-white"
                    : "bg-transparent text-[#202020]"
                    }`}
                >
                  Kroolo Search
                </button>
              </div>
            </div>
          </div>
        </div>

        {selectedProduct === "work" && (
          <div className="container relative z-10 mx-auto -mt-16 px-4 xl:px-6">
            <PricingCardsArea />
          </div>
        )}
        {selectedProduct === "search" && (
          <div className="container relative z-10 mx-auto -mt-16 px-4 xl:px-6">
            <div className="mx-auto max-w-[800px]">
              <SearchPricingCardsArea />
              <section className="relative z-10 bg-white py-16">
                <div className="container mx-auto px-4 text-center xl:max-w-[875px] xl:px-6">
                  <h2 className="mb-4 text-[24px] font-bold text-[#000000] lg:text-[32px]">
                    Ready to experience the power of intelligent{" "}
                    <span className="bg-gradient-to-r from-[#781E8F] to-[#422CBB] bg-clip-text text-transparent">
                      Enterprise Search?
                    </span>
                  </h2>
                  <div className="mt-4 flex justify-center">
                    <CustomButton
                      text="Talk to Sales"
                      url={BookDemoHubspotButtonUrl}
                      variant="primary"
                      version="black"
                    />
                  </div>
                </div>
              </section>
            </div>
          </div>
        )}
      </section>
      {selectedProduct === "work" && (
        <>
          <ComparePlans />
          <Achievements />
          <CostCalculator />
          <AiToolsFaq
            faqTitle="Frequently Asked Questions on Kroolo Pricing"
            faqList={pricingFaqList}
          />
        </>
      )}
    </>
  );
};

export default Pricing;
